import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Modal,
  TextField,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import logo from "../assets/logo-white.png";
import bookingIcon from "../assets/phoenwithcc.png";
import api from '../services/api';
import { useAuthContext } from "../services/AuthContext";
import Ellipse4054 from "../assets/Ellipse4054.png";
import heroImage1 from "../assets/heroimage1.png";

function PaymentSuccess() {
  const [callSchedule, setCallSchedule] = useState({ date: "", time: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { token, logout: authLogout } = useAuthContext();

  const selectedClaimDetails = location.state?.selectedClaimDetails || [];
  const orderId = location.state?.orderId || null;

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleScheduleCall = async () => {
    if (!callSchedule.date || !callSchedule.time) {
      setError("Please select a date and time for the call.");
      alert("Please select a date and time.");
      return;
    }

    setIsLoading(true);
    try {
      const orderIds = await Promise.all(
        selectedClaimDetails.map(async (claim) => {
          const response = await api.get('/orders/order-history', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const order = response.data.orders.find(
            (o) => o.label_claim_id === Number(claim.id)
          );
          if (!order) {
            console.warn(`No order found for claim ID ${claim.id}`);
            return null;
          }
          if (order.status === "fully_paid") {
            console.log(`Claim ID ${claim.id} is fully paid. Redirecting to finalSuccess.`);
            navigate("/final-success", { state: { selectedClaimDetails } });
            return null;
          }
          if (order.status !== "booking_paid") {
            console.warn(`No booking_paid order found for claim ID ${claim.id}. Current status: ${order.status}`);
            return null;
          }
          return order.id;
        })
      );

      const validOrderIds = orderIds.filter((id) => id);
      if (!validOrderIds.length) {
        const claimIds = selectedClaimDetails.map((c) => c.id).join(", ");
        throw new Error(`No valid booking orders found for claim IDs: ${claimIds}.`);
      }

      await api.post(
        '/orders/schedule-call',
        {
          order_ids: validOrderIds,
          date: callSchedule.date,
          time: callSchedule.time,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      navigate("/call-schedule-success", { state: { selectedClaimDetails, orderId: validOrderIds[0] } });
    } catch (error) {
      console.error("Failed to schedule call:", error);
      const errorMessage = error.response?.data?.detail || error.message || "An unexpected error occurred.";
      setError(errorMessage);
      alert(`Failed to schedule call: ${errorMessage}`);
      if (error.response?.status === 401) {
        authLogout();
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleCancel = () => {
    navigate("/dashboard");
  };

  const handleLogout = () => {
    authLogout();
    navigate("/login");
  };

  if (!selectedClaimDetails.length) {
    navigate("/dashboard");
    return null;
  }

  const menuItems = [
    { text: "Label Claims", onClick: () => navigate("/dashboard") },
    { text: "Order Details", onClick: () => navigate("/dashboard?view=orderDetails") },
    { text: "User Settings", onClick: () => navigate("/user-settings") },
    { text: "Logout", onClick: handleLogout },
  ];

  return (
    <Box
      sx={{
        fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#FFFFFF",
      }}
    >
      <AppBar
        position="sticky"
        sx={{
          background: `url(${heroImage1}) no-repeat center/cover`,
          boxShadow: "none",
          padding: { xs: "10px 20px", md: "10px 117px" },
          height: { xs: "200px", md: "260px" },
          zIndex: 1,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "14px",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: { xs: "80px", md: "118px" },
              height: { xs: "45px", md: "67px" },
              objectFit: "contain",
            }}
          />
          
          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: "20px",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              Label Claims
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard?view=orderDetails")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              Order Details
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate("/user-settings")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              User Settings
            </Button>
          </Box>
          
          {/* Desktop Logout Button */}
          <Button
            color="inherit"
            onClick={handleLogout}
            sx={{
              display: { xs: "none", md: "block" },
              color: "#FFFFFF",
              textTransform: "uppercase",
              fontSize: "16px",
              fontWeight: 700,
              fontFamily: "'Lato', sans-serif",
              lineHeight: "200%",
              padding: "8px 16px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            Logout
          </Button>

          {/* Mobile Menu Icon */}
          <IconButton
            color="inherit"
            onClick={toggleMobileMenu}
            sx={{ display: { xs: "block", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
        
        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={toggleMobileMenu}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              background: "linear-gradient(90deg, #66202D 0%, #F89622 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "10px",
            }}
          >
            <IconButton onClick={toggleMobileMenu} sx={{ color: "#FFFFFF" }}>
              <MenuIcon />
            </IconButton>
          </Box>
          <List sx={{ padding: "20px" }}>
            {menuItems.map((item, index) => (
              <ListItem
                button
                key={index}
                onClick={() => {
                  item.onClick();
                  toggleMobileMenu();
                }}
                sx={{
                  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    sx: {
                      color: "#FFFFFF",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "18px",
                      fontWeight: 400,
                      textAlign: "center",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Drawer>

        <Box
          sx={{
            height: { xs: "calc(200px - 64px)", md: "calc(260px - 64px)" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            textAlign: "center",
            marginBottom: "50px",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "36px", md: "48px" },
              lineHeight: "90%",
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              textTransform: "none",
            }}
          >
            Book Label Claim {selectedClaimDetails[0]?.id || ""}
          </Typography>
        </Box>
      </AppBar>


      <Box sx={{ marginTop: "-70px", zIndex: 2, flex: 1, maxWidth:"100%", padding: { xs: "20px", md: "0px 117px" } }}>
        <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              border: "1px solid #ECECEC",
              boxShadow: "0px 14px 24px rgba(0, 0, 0, 0.05)",
              padding: "30px",
              width: "100%",
              height: "fit-content",
              position: "relative",
              
            }}
            >
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #F5D9B0",
            boxShadow: "none",
            padding: { xs: "20px", md: "40px" },
            maxWidth: { xs: "100%", md: "400px" },
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          {error && (
            <Typography
              sx={{
                color: "red",
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: { xs: "14px", md: "16px" },
                marginBottom: "20px",
              }}
            >
              {error}
            </Typography>
          )}
          
          <Box
            sx={{
              width: { xs: "120px", md: "150px" },
              height: { xs: "120px", md: "150px" },
              borderRadius: "50%",
              backgroundColor: "#FEEBD9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "auto",
              marginBottom: "24px",
            }}
          >
            <img
              src={bookingIcon}
              alt="Booking Icon"
              style={{
                width: { xs: "60px", md: "80px" },
                height: { xs: "60px", md: "80px" },
                objectFit: "contain",
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              fontSize: { xs: "20px", md: "24px" },
              fontWeight: 500,
              color: "#1E1E1E",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            Congratulations
          </Typography>
          <Typography
            sx={{
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 400,
              color: "#1E1E1E",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            Check confirmation on your registered email.
          </Typography>
          
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
            <Button
              onClick={handleOpenModal}
              sx={{
                backgroundColor: "#66202D",
                color: "#FFFFFF",
                borderRadius: "390px",
                padding: { xs: "10px 20px", md: "15px 30px" },
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: 500,
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: "#551A25",
                },
              }}
            >
              Schedule Call
            </Button>
          </Box>
        </Box>
      </Box>
      </Box>  
      {/* Modal for Scheduling */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: 500 },
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              fontSize: { xs: "20px", md: "24px" },
              fontWeight: 500,
              color: "#1E1E1E",
              textAlign: "center",
              mb: 3,
            }}
          >
            Schedule a Call
          </Typography>
          <Typography
            sx={{
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: 400,
              color: "#1E1E1E",
              textAlign: "center",
              mb: 3,
            }}
          >
            Please select a date and time for our team to discuss your requirements.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <TextField
              label="Select Date"
              type="date"
              value={callSchedule.date}
              onChange={(e) => setCallSchedule({ ...callSchedule, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  fontSize: { xs: "14px", md: "16px" },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  color: "#AEA9A6",
                  fontSize: { xs: "14px", md: "16px" },
                },
              }}
              fullWidth
            />
            <TextField
              label="Select Time"
              type="time"
              value={callSchedule.time}
              onChange={(e) => setCallSchedule({ ...callSchedule, time: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  fontSize: { xs: "14px", md: "16px" },
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  color: "#AEA9A6",
                  fontSize: { xs: "14px", md: "16px" },
                },
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: "20px", mt: 4 }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                border: "2px solid #66202D",
                borderRadius: "390px",
                padding: { xs: "8px 16px", md: "10px 20px" },
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 500,
                color: "#1E1E1E",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: "rgba(102, 32, 45, 0.1)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleCall}
              disabled={isLoading}
              sx={{
                backgroundColor: "#66202D",
                color: "#FFFFFF",
                borderRadius: "390px",
                padding: { xs: "8px 16px", md: "10px 20px" },
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 500,
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: "#551A25",
                },
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {isLoading && <CircularProgress size={20} color="inherit" />}
              Confirm Schedule
            </Button>
          </Box>
        </Box>
      </Modal>

      <Box
        sx={{
          padding: { xs: "20px", md: "20px 136px" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          mt: "auto",
          textAlign: { xs: "center", md: "left" },
          gap: { xs: "10px", md: "0" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Lato', sans-serif",
            fontSize: { xs: "16px", md: "18px" },
            fontWeight: 400,
            color: "#1E1E1E",
          }}
        >
          © 2025 DealSphere. All rights reserved.
        </Typography>
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 400,
              textTransform: "none",
            }}
          >
            Terms
          </Button>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 400,
              textTransform: "none",
            }}
          >
            Privacy
          </Button>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 400,
              textTransform: "none",
            }}
          >
            Contact
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PaymentSuccess;